import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Compass, Home, ScanLine, ShoppingBag, UserCircle } from "lucide-react";

import { BrandMark } from "@/components/cards";
import { useShop } from "@/shop";

export const TABS = [
  { to: "/home", title: "Home", icon: Home, key: "home" },
  { to: "/scan", title: "Scan", icon: ScanLine, key: "scan" },
  { to: "/explore", title: "Explore", icon: Compass, key: "explore" },
  { to: "/mystyle", title: "My Style", icon: UserCircle, key: "mystyle" },
  { to: "/shop", title: "Shop", icon: ShoppingBag, key: "shop" },
];

function TopNav() {
  const { cart } = useShop();
  const navigate = useNavigate();
  const n = cart?.total_quantity ?? 0;
  return (
    <header className="sticky top-0 z-40 hidden border-b border-divider bg-glass md:block">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-8 px-6">
        <button type="button" data-testid="nav-logo" onClick={() => navigate("/home")} className="focus-ring rounded-md">
          <BrandMark height={28} testId="nav-wordmark" className="font-display text-[22px] tracking-[6px] text-navy" />
        </button>
        <nav aria-label="Main" className="flex flex-1 items-center gap-1">
          {TABS.map((t) => (
            <NavLink
              key={t.key}
              to={t.to}
              data-testid={`topnav-${t.key}`}
              className={({ isActive }) => `focus-ring rounded-full px-4 py-2 font-body-bold text-xs uppercase tracking-[1px] transition-colors ${isActive ? "bg-navy text-white" : "text-mute hover:bg-tert hover:text-navy"}`}
            >
              {t.title}
            </NavLink>
          ))}
        </nav>
        <button type="button" data-testid="topnav-cart" aria-label={`Cart, ${n} items`} onClick={() => navigate("/cart")} className="focus-ring relative flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-navy text-navy transition-colors hover:bg-navy hover:text-white">
          <ShoppingBag className="h-5 w-5" aria-hidden />
          {n ? <span data-testid="topnav-cart-badge" className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-ocean px-1 font-body-bold text-[10px] text-white">{n}</span> : null}
        </button>
      </div>
    </header>
  );
}

function BottomTabs() {
  const { cart } = useShop();
  const n = cart?.total_quantity ?? 0;
  return (
    <nav aria-label="Tabs" className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
      <div className="flex h-16">
        {TABS.map((t) => (
          <NavLink key={t.key} to={t.to} data-testid={`tab-${t.key}`} className={({ isActive }) => `focus-ring relative flex flex-1 flex-col items-center justify-center gap-0.5 ${isActive ? "text-navy" : "text-mute"}`}>
            {({ isActive }) => (
              <>
                <t.icon className="h-[23px] w-[23px]" strokeWidth={isActive ? 2.5 : 1.8} aria-hidden />
                <span className="font-body-bold text-[10px] uppercase tracking-[0.4px]">{t.title}</span>
                {t.key === "shop" && n ? <span data-testid="tab-shop-badge" className="absolute right-[calc(50%-22px)] top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-ocean px-1 font-body-bold text-[10px] text-white">{n}</span> : null}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** App chrome: top nav on desktop for every page; bottom tab bar on phones for the 5 tab pages only (like the app). */
export function AppLayout() {
  const { pathname } = useLocation();
  const isTab = TABS.some((t) => pathname === t.to);
  return (
    <div className="min-h-screen bg-off">
      <TopNav />
      <main className={isTab ? "pb-20 md:pb-0" : ""}>
        <Outlet />
      </main>
      {isTab ? <BottomTabs /> : null}
    </div>
  );
}

/** Centered page column. */
export function Page({ children, testId, className = "", wide }) {
  return (
    <div data-testid={testId} className={`mx-auto w-full ${wide ? "max-w-6xl" : "max-w-3xl"} px-5 md:px-6 ${className}`}>
      {children}
    </div>
  );
}
